import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useAuth } from '../../context/AuthContext';
import './UserDatabase.css';

const API_BASE_URL = 'https://api.avtechbmt.cloud/api/v1/auth';

const getToken = (token) => token || localStorage.getItem('token');

export default function UserDatabase() {
  const { user, token } = useAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [deletingId, setDeletingId] = useState(null);

  const abortRef = useRef(null);

  const currentUserId = user?._id || user?.id;
  const isAdmin = user?.role === 'admin';

  /* ------------------------------ Fetch users ------------------------------ */
  const fetchUsers = useCallback(async () => {
    if (!isAdmin) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE_URL}/users`, {
        headers: {
          Authorization: `Bearer ${getToken(token)}`,
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      });

      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error(
          `Server returned non-JSON response (${res.status}). Check backend URL.`
        );
      }

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to fetch users');

      setUsers(data.users || []);
    } catch (err) {
      if (err.name !== 'AbortError') setError(err.message);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [isAdmin, token]);

  useEffect(() => {
    if (isAdmin) fetchUsers();
    return () => abortRef.current?.abort();
  }, [isAdmin, fetchUsers]);

  /* --------------------------------- Delete -------------------------------- */
  const handleDeleteUser = useCallback(
    async (userId) => {
      if (
        !window.confirm('Are you sure you want to revoke access for this user?')
      )
        return;

      setError('');
      setSuccess('');
      setDeletingId(userId);

      const snapshot = users;
      // Optimistic removal
      setUsers((prev) => prev.filter((u) => (u._id || u.id) !== userId));

      try {
        const res = await fetch(`${API_BASE_URL}/users/${userId}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${getToken(token)}`,
            'Content-Type': 'application/json',
          },
        });

        const contentType = res.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
          throw new Error(`Server returned non-JSON response (${res.status}).`);
        }

        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Failed to delete user');

        setSuccess('User session access revoked successfully.');
        setTimeout(() => setSuccess(''), 4000);
      } catch (err) {
        setUsers(snapshot); // rollback
        setError(err.message);
      } finally {
        setDeletingId(null);
      }
    },
    [token, users]
  );

  /* -------------------------------- Filtering ------------------------------ */
  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter((u) => {
      const matchesQuery =
        !q ||
        u.name?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q) ||
        u.phone?.toLowerCase().includes(q);
      const matchesRole = roleFilter === 'all' || u.role === roleFilter;
      return matchesQuery && matchesRole;
    });
  }, [users, query, roleFilter]);

  /* ------------------------------ Render guard ----------------------------- */
  if (!isAdmin) {
    return (
      <div className="user-database-container">
        <div className="user-database-alert user-database-alert-danger">
          ⚠️ You do not have permission to view this page.
        </div>
      </div>
    );
  }

  return (
    <div className="user-database-container">
      {/* Header */}
      <header className="user-database-header">
        <div>
          <span className="user-database-subtitle-label">
            Admin Control Panel
          </span>
          <h2 className="user-database-title">User Database</h2>
          <p className="user-database-description">
            Manage registered accounts, inspect permissions, and revoke access.
          </p>
        </div>
        <button
          className="user-database-btn-secondary"
          onClick={fetchUsers}
          disabled={loading}
          aria-label="Refresh user directory"
        >
          <span aria-hidden="true">🔄</span>
          <span>{loading ? 'Refreshing…' : 'Refresh'}</span>
        </button>
      </header>

      {/* Toolbar */}
      <div className="user-database-toolbar">
        <input
          type="search"
          className="user-database-search"
          placeholder="Search by name, email, or phone…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search users"
        />
        <select
          className="user-database-select"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          aria-label="Filter by role"
        >
          <option value="all">All roles</option>
          <option value="admin">Admin</option>
          <option value="user">User</option>
        </select>
      </div>

      {/* Alerts */}
      {error && (
        <div className="user-database-alert user-database-alert-danger" role="alert">
          ⚠️ {error}
        </div>
      )}
      {success && (
        <div className="user-database-alert user-database-alert-success" role="status">
          ✅ {success}
        </div>
      )}

      {/* Content */}
      <div className="user-database-table-card">
        {loading ? (
          <SkeletonRows />
        ) : filteredUsers.length === 0 ? (
          <div className="user-database-empty-state">
            {users.length === 0
              ? 'No users registered yet.'
              : 'No users match your search.'}
          </div>
        ) : (
          <>
            {/* Desktop / tablet table */}
            <div className="user-database-table-wrapper">
              <table className="user-database-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Role</th>
                    <th className="user-database-text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u) => {
                    const id = u._id || u.id;
                    const isSelf = currentUserId === id;
                    const isDeleting = deletingId === id;

                    return (
                      <tr key={id}>
                        <td className="user-database-cell-name">
                          {u.name || 'N/A'}
                        </td>
                        <td className="user-database-cell-email">{u.email}</td>
                        <td>{u.phone || '—'}</td>
                        <td>
                          <RoleBadge role={u.role} />
                        </td>
                        <td className="user-database-text-right">
                          {isSelf ? (
                            <span className="user-database-self-indicator">
                              You
                            </span>
                          ) : (
                            <button
                              className="user-database-btn-delete"
                              onClick={() => handleDeleteUser(id)}
                              disabled={isDeleting}
                              aria-label={`Revoke access for ${u.name || u.email}`}
                            >
                              {isDeleting ? 'Revoking…' : 'Revoke Access'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile card list */}
            <ul className="user-database-card-list" role="list">
              {filteredUsers.map((u) => {
                const id = u._id || u.id;
                const isSelf = currentUserId === id;
                const isDeleting = deletingId === id;

                return (
                  <li key={id} className="user-database-user-card">
                    <div className="user-database-user-card-head">
                      <div className="user-database-user-card-identity">
                        <span className="user-database-cell-name">
                          {u.name || 'N/A'}
                        </span>
                        <RoleBadge role={u.role} />
                      </div>
                      {isSelf && (
                        <span className="user-database-self-indicator">
                          You
                        </span>
                      )}
                    </div>

                    <dl className="user-database-user-card-meta">
                      <div>
                        <dt>Email</dt>
                        <dd>{u.email}</dd>
                      </div>
                      <div>
                        <dt>Phone</dt>
                        <dd>{u.phone || '—'}</dd>
                      </div>
                    </dl>

                    {!isSelf && (
                      <button
                        className="user-database-btn-delete user-database-btn-block"
                        onClick={() => handleDeleteUser(id)}
                        disabled={isDeleting}
                      >
                        {isDeleting ? 'Revoking…' : 'Revoke Access'}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      {/* Footer count */}
      {!loading && users.length > 0 && (
        <p className="user-database-footer-note">
          Showing {filteredUsers.length} of {users.length} user
          {users.length !== 1 ? 's' : ''}
        </p>
      )}
    </div>
  );
}

/* --------------------------- Small subcomponents --------------------------- */

function RoleBadge({ role }) {
  const isAdmin = role === 'admin';
  return (
    <span
      className={`user-database-role-badge ${
        isAdmin ? 'user-database-role-admin' : 'user-database-role-user'
      }`}
    >
      {role || 'user'}
    </span>
  );
}

function SkeletonRows() {
  return (
    <div className="user-database-skeleton" aria-hidden="true">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="user-database-skeleton-row">
          <span className="user-database-skeleton-cell wide" />
          <span className="user-database-skeleton-cell" />
          <span className="user-database-skeleton-cell narrow" />
          <span className="user-database-skeleton-cell pill" />
        </div>
      ))}
    </div>
  );
}